/*
 *  TTTech nerve-management-system
 *  Copyright(c) 2022. TTTech Industrial Automation AG.
 *
 *  ALL RIGHTS RESERVED.
 *
 *  Usage of this software, including source code, netlists, documentation,
 *  is subject to restrictions and conditions of the applicable license
 *  agreement with TTTech Industrial Automation AG or its affiliates.
 *
 *  All trademarks used are the property of their respective owners.
 *
 *  TTTech Industrial Automation AG and its affiliates do not assume any liability
 *  arising out of the application or use of any product described or shown
 *  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
 *  make changes, at any time, in order to improve reliability, function or
 *  design.
 *
 *  Contact Information:
 *  support@tttech-industrial.com
 *
 *  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
 *
 */
import BaseApiService from './base.api-service';

const PATH = 'nerve/workload';
const PATHV2 = 'nerve/v2/workloads';
const PATHV3 = 'nerve/v3/workloads';
const PATHS = {
  DELETE: 'nerve/workload',
  CANCEL: 'nerve/workload',
  DISABLE_ENABLE: 'nerve/workload/disable',
  UPDATE_RESOURCES: 'nerve/workload/updateResources',
  DEPLOYED_WORKLOAD: 'nerve/workload/deployedWorkload',
  CHECK_FILE: 'nerve/workload/check',
};
class WorkloadsApiService extends BaseApiService {
  createNewWorkloadVersion(param) {
    const options = {
      headers: { 'Content-Type': undefined },
      onUploadProgress: (e) => {
        const progressPercentage = (e.loaded / e.total) * 100;
        param.cb(progressPercentage);
      },
      cancelToken: param.token,
    };
    return this.post(`${PATHV2}`, param.payload, options, { authRequired: true });
  }

  updateVersion(param) {
    const options = {
      headers: { 'Content-Type': undefined },
      onUploadProgress: (e) => {
        const progressPercentage = (e.loaded / e.total) * 100;
        param.cb(progressPercentage);
      },
      cancelToken: param.token,
    };
    return this.patch(`${PATHV2}`, param.payload, options, { authRequired: true });
  }

  getAllDeployedWorkloads({ serialNumber, cancelToken }) {
    const options = {
      cancelToken,
    };
    return this.get(`${PATH}/node/${serialNumber}/devices`, options, { authRequired: true });
  }

  setCommand(data) {
    return this.post(`${PATH}/controller`, data, { authRequired: true });
  }

  fetchWorkloads(params) {
    params.order = JSON.stringify(params.order);
    return this.get(PATHV2, { params }, { authRequired: true });
  }

  deleteWorkload(param) {
    return this.delete(`${PATHS.DELETE}/${param._id}`, { authRequired: true });
  }

  disableEnableWorkload(param) {
    return this.patch(`${PATHS.DISABLE_ENABLE}/${param._id}`, param, { authRequired: true });
  }

  getWlById({ id, ignoreInterceptor }) {
    return this.get(`${PATHV2}/${id}`, { authRequired: true, ignoreInterceptor });
  }

  getWlByIdV3({ id, ignoreInterceptor }) {
    return this.get(`${PATHV3}/${id}`, { authRequired: true, ignoreInterceptor });
  }

  updateResources(data) {
    return this.post(PATHS.UPDATE_RESOURCES, data, { authRequired: true });
  }

  getWorkloadVersions({ id, ignoreInterceptor }) {
    return this.get(`${PATHS.DEPLOYED_WORKLOAD}/${id}`, { authRequired: true, ignoreInterceptor });
  }

  deleteVersion(param) {
    return this.delete(`${PATHV2}/${param.wlId}/versions/${param.verId}`, { authRequired: true });
  }

  canceledVersionCreation(param) {
    return this.post(`${PATHS.CANCEL}/${param.wlId}/version/${param.verId}`, {}, { authRequired: true });
  }

  getWorkloadVersionRc(param) {
    return this.get(`${PATH}/${param.workloadId}/version/${param.versionId}/remoteConnections`, {
      authRequired: true,
    });
  }

  checkFiles(param) {
    return this.get(`${PATHS.CHECK_FILE}/${param.wlId}/${param.verId}`);
  }

  getUniqueInfo(params) {
    return this.get(`${PATHV2}/${params.workloadId}/versions/${params.versionId}/unique-info`, {
      authRequired: true,
    });
  }

  exportWorkloadV3(params) {
    return this.post(
      `${PATHV3}/${params.workloadId}/versions/${params.versionId}/export-init`,
      {},
      { authRequired: true },
    );
  }

  getWorkloadUsingV3Api({ id, ignoreInterceptor }) {
    return this.get(`${PATHV3}/${id}`, { authRequired: true, ignoreInterceptor });
  }

  getVersionListUsingV3Api(id) {
    return this.get(`${PATHV3}/${id}/versions`, { authRequired: true });
  }

  addFile(param) {
    return this.post(`${PATHV3}/${param.wlId}/versions/${param.verId}/files`, param.data, {
      authRequired: true,
    });
  }

  editFile(param) {
    return this.patch(`${PATHV3}/${param.wlId}/versions/${param.verId}/files/${param.fileId}`, param.data, {
      authRequired: true,
    });
  }

  defineAllFiles(param) {
    return this.post(`${PATHV3}/${param.wlId}/versions/${param.verId}/define-all-files`, param.data, {
      authRequired: true,
    });
  }

  fetchFiles(param) {
    return this.get(`${PATHV3}/${param.wlId}/versions/${param.verId}/files`, {
      authRequired: true,
    });
  }

  checkComposeFile(data) {
    return this.post(`${PATHV3}/compose`, data, { authRequired: true });
  }

  fetchComposeFile(param) {
    return this.get(`${PATHV3}/compose/${param.wlId}/versions/${param.verId}/files/${param.fileId}`, {
      authRequired: true,
    });
  }

  cancelComposeWorkloadVersionCreation(payload) {
    return this.patch(`${PATHV3}/${payload.wlId}/versions/${payload.verId}/files/cancel`, {}, { authRequired: true });
  }

  deleteWorkloadVersionUsingV3Api(param) {
    return this.delete(`${PATHV3}/${param.wlId}/versions/${param.verId}`, { authRequired: true });
  }

  createWorkloadUsingV3Api(param) {
    const options = {
      headers: { 'Content-Type': undefined },
      onUploadProgress: (e) => {
        const progressPercentage = (e.loaded / e.total) * 100;
        param.cb(progressPercentage);
      },
      cancelToken: param.token,
    };
    return this.post(`${PATHV3}`, param.payload, options, { authRequired: true });
  }

  updateWorkloadUsingV3Api({ id, data }) {
    return this.patch(`${PATHV3}/${id}`, data, { authRequired: true });
  }

  deleteWorkloadUsingV3Api(param) {
    return this.delete(`${PATHV3}/${param._id}`, { authRequired: true });
  }

  fetchWorkloadVersionByIdUsingV3Api(payload) {
    return this.get(`${PATHV3}/${payload.wlId}/versions/${payload.verId}`, {
      authRequired: true,
      ignoreInterceptor: payload.ignoreInterceptor,
    });
  }

  createWorkloadVersionUsingV3Api({ payload, token, cb }) {
    const options = {
      headers: { 'Content-Type': undefined },
      onUploadProgress: (e) => {
        const progressPercentage = (e.loaded / e.total) * 100;
        cb(progressPercentage);
      },
      cancelToken: token,
    };
    return this.post(`${PATHV3}/${payload.wlId}/versions`, payload.data, options, {
      authRequired: true,
    });
  }

  updateWorkloadVersionUsingV3Api({ payload, token, cb }) {
    const options = {
      headers: { 'Content-Type': undefined },
      onUploadProgress: (e) => {
        const progressPercentage = (e.loaded / e.total) * 100;
        cb(progressPercentage);
      },
      cancelToken: token,
    };
    return this.patch(`${PATHV3}/${payload.wlId}/versions/${payload.verId}`, payload.data, options, {
      authRequired: true,
    });
  }
}

export default new WorkloadsApiService();
